import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Icons } from '@/components/icons';
import { useAuth, useUser } from '@/firebase';
import { useLoginForm } from '@/hooks/use-login-form';
import { getMarketingUrl } from '@/lib/marketing-url';

const loginSearchSchema = z.object({
  inviteToken: z.string().optional(),
  lang: z.string().optional(),
});

export const Route = createFileRoute('/login')({
  component: LoginPage,
  validateSearch: (search) => loginSearchSchema.parse(search),
});

function LoginPage() {
  const { t, i18n } = useTranslation();
  const auth = useAuth();
  const navigate = useNavigate();
  const { user, isUserLoading } = useUser();
  const search = Route.useSearch();

  const {
    email,
    setEmail,
    password,
    setPassword,
    tabIndex,
    setTabIndex,
    loading,
    handleSignUp,
    handleSignIn,
    handleGoogleSignIn,
    handleAppleSignIn,
    handleForgotPassword,
  } = useLoginForm(auth);

  const isPasswordAuthEnabled = import.meta.env.VITE_ENABLE_PASSWORD_AUTH === 'true';
  const [isPasswordResetOpen, setIsPasswordResetOpen] = useState(false);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  useEffect(() => {
    if (user) {
      navigate({ to: '/', search: { inviteToken: search.inviteToken, lang: search.lang } });
    }
  }, [user, navigate, search.inviteToken, search.lang]);

  if (isUserLoading || user) {
    return (
      <Box sx={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  const isAnyLoading = loading !== null;

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (isAnyLoading || !isPasswordAuthEnabled) {
      return;
    }

    if (tabIndex === 0) {
      void handleSignIn();
    } else {
      void handleSignUp();
    }
  };

  const handlePasswordResetSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const wasSent = await handleForgotPassword();
    if (wasSent) {
      setIsPasswordResetOpen(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', gap: 3 }}>
        <Icons.logo className="w-16 h-16 text-primary" />
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            {t('login.welcome')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('login.signInToStart')}
          </Typography>
        </Box>

        <Box sx={{ width: '100%', maxWidth: 400 }}>
          {isPasswordAuthEnabled && (
            <Tabs
              value={tabIndex}
              onChange={handleTabChange}
              variant="fullWidth"
              sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
            >
              <Tab label={t('login.signIn')} disabled={isAnyLoading} />
              <Tab label={t('login.signUp')} disabled={isAnyLoading} />
            </Tabs>
          )}

          <Card component="form" onSubmit={handleSubmit} sx={{ borderRadius: 2, boxShadow: 3 }}>
            <CardHeader
              title={!isPasswordAuthEnabled ? t('login.signIn') : (tabIndex === 0 ? t('login.signIn') : t('login.signUp'))}
              subheader={!isPasswordAuthEnabled ? t('login.chooseMethod') : (tabIndex === 0 ? t('login.enterCredentials') : t('login.createAccount'))}
              titleTypographyProps={{ variant: 'h5', fontWeight: 600 }}
            />
            {isPasswordAuthEnabled && (
              <CardContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    label={t('login.email')}
                    type="email"
                    placeholder="m@example.com"
                    fullWidth
                    variant="outlined"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    required
                    disabled={isAnyLoading}
                  />
                  <TextField
                    label={t('login.password')}
                    type="password"
                    fullWidth
                    variant="outlined"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete={tabIndex === 0 ? 'current-password' : 'new-password'}
                    required
                    disabled={isAnyLoading}
                  />
                  {tabIndex === 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: -1 }}>
                      <Button
                        type="button"
                        variant="text"
                        size="small"
                        onClick={() => setIsPasswordResetOpen(true)}
                        sx={{ textTransform: 'none', fontWeight: 500 }}
                        disabled={isAnyLoading}
                      >
                        {loading === 'forgot' ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          t('login.recoverPassword')
                        )}
                      </Button>
                    </Box>
                  )}
                </Box>
              </CardContent>
            )}
            <CardActions sx={{ flexDirection: 'column', gap: 2, px: 3, pb: 4, pt: isPasswordAuthEnabled ? 0 : 2 }}>
              {isPasswordAuthEnabled && tabIndex === 0 && (
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  size="large"
                  sx={{ borderRadius: 2 }}
                  disabled={isAnyLoading}
                >
                  {loading === 'signIn' ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    t('login.signInEmail')
                  )}
                </Button>
              )}
              {isPasswordAuthEnabled && tabIndex === 1 && (
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  size="large"
                  sx={{ borderRadius: 2 }}
                  disabled={isAnyLoading}
                >
                  {loading === 'signUp' ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    t('login.signUpEmail')
                  )}
                </Button>
              )}

              <Divider sx={{ width: '100%', my: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ px: 1, textTransform: 'uppercase', letterSpacing: 1 }}>
                  {t('login.orContinueWith')}
                </Typography>
              </Divider>

              <Button
                type="button"
                onClick={handleGoogleSignIn}
                variant="outlined"
                fullWidth
                size="large"
                sx={{ borderRadius: 2 }}
                startIcon={loading === 'google' ? <CircularProgress size={20} color="inherit" /> : <Icons.google className="w-5 h-5" />}
                disabled={isAnyLoading}
              >
                {loading === 'google' ? null : t('login.signInGoogle')}
              </Button>

              <Box sx={{ mt: 0.5, width: '100%' }}>
                <Button
                  type="button"
                  onClick={handleAppleSignIn}
                  variant="outlined"
                  fullWidth
                  size="large"
                  sx={{ borderRadius: 2 }}
                  startIcon={loading === 'apple' ? <CircularProgress size={20} color="inherit" /> : <Icons.apple className="w-5 h-5" />}
                  disabled={isAnyLoading}
                >
                  {loading === 'apple' ? null : t('login.signInApple')}
                </Button>
              </Box>
              <Typography variant="caption" color="text.secondary" textAlign="center">
                {t('loginLegal.prefix')}{' '}
                <Link href={getMarketingUrl(i18n.resolvedLanguage ?? i18n.language, 'terms')} target="_blank" rel="noreferrer">
                  {t('loginLegal.terms')}
                </Link>{' '}{t('loginLegal.and')}{' '}
                <Link href={getMarketingUrl(i18n.resolvedLanguage ?? i18n.language, 'privacy')} target="_blank" rel="noreferrer">
                  {t('loginLegal.privacy')}
                </Link>.
              </Typography>
            </CardActions>
          </Card>

          <Dialog
            open={isPasswordResetOpen}
            onClose={loading === 'forgot' ? undefined : () => setIsPasswordResetOpen(false)}
            fullWidth
            maxWidth="xs"
            PaperProps={{ component: 'form', onSubmit: handlePasswordResetSubmit }}
          >
            <DialogTitle>{t('login.resetPasswordTitle')}</DialogTitle>
            <DialogContent>
              <DialogContentText sx={{ mb: 2 }}>
                {t('login.resetPasswordDescription')}
              </DialogContentText>
              <TextField
                autoFocus
                label={t('login.email')}
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                autoComplete="email"
                required
                disabled={loading === 'forgot'}
                fullWidth
              />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3 }}>
              <Button
                type="button"
                onClick={() => setIsPasswordResetOpen(false)}
                disabled={loading === 'forgot'}
              >
                {t('common.cancel')}
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={loading === 'forgot' || !email.trim()}
                startIcon={loading === 'forgot' ? <CircularProgress size={18} color="inherit" /> : null}
              >
                {t('login.sendResetLink')}
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Box>
    </Container>
  );
}
